import React, {useContext, useEffect, useState} from 'react';
import axios from 'axios';
import AuthContext from '../../authentication/AuthContext';
import UserContext from '../../user/UserContext';
import {useAlert} from 'react-alert';
import {Button, Fade, FormControl} from 'react-bootstrap';
import {IconWrapper} from '../IconWrapper';
import {faFilter, faSearch, faSyncAlt} from '@fortawesome/free-solid-svg-icons';
import {CSSTransition} from 'react-transition-group';

import SearchFiltersPane from "./searchFiltersPane/SearchFiltersPane";

import styles from './productSearchWithFiltersPane.module.scss';

export default function ProductSearchWithFiltersPane({
                                                         hideFilters,
                                                         showCount,
                                                         resultCount,
                                                         supplierCode,
                                                         setLoading,
                                                         searched,
                                                         setSearched,
                                                         setSearchResults,
                                                         style
                                                     }) {

    const cas = useContext(AuthContext);
    const {userDetails} = useContext(UserContext);
    const alert = useAlert();

    const currentSupplierCode = supplierCode ? supplierCode : 'ALL';

    const initialCategories = {
        superCategory: '',
        category: ''
    }

    const initialFilters = {
        isPromoOnly: false,
        isCoopOwnLabel: false,
        isHeritageOwnLabel: false,
        isOrganic: null,
        isGlutenFree: null,
        isVegetarian: null,
        isFairtrade: null,
        isHalal: null,
        isVegan: null
    }

    const [allSupplierCategories, setAllSupplierCategories] = useState();

    const [categories, setCategories] = useState(initialCategories);
    const [filters, setFilters] = useState(initialFilters);
    const [searchTerm, setSearchTerm] = useState('');
    const [isFiltersPaneOpen, setIsFiltersPaneOpen] = useState(false);

    const allFiltersCleared = Object.keys(filters).every(key => filters[key] === null || filters[key] === false);
    const categorySelected = categories.category !== '';
    const superCategorySelected = categories.superCategory !== '';

    const getCategories = () => {
        axios
            .get(window.CONFIG['ws.activoc-web-order-api.categories.suppliercategories.full']
                    .replace('{supplierCode}', currentSupplierCode),
                {
                    headers: {
                        Authorization: cas,
                        'Content-Type': 'application/json'
                    }
                })
            .then(response => setAllSupplierCategories(() => response.data));
    };

    const handleSubmit = () => {
        if (userDetails.currentFranchise) {
            if (searchTerm !== '' || superCategorySelected || categorySelected || !allFiltersCleared) {
                setLoading(true);

                // Let GTM know about form submission
                const dataLayer = window.dataLayer || [];
                dataLayer.push({
                    "event": "gtm.formSubmit",
                    "form": "productSearch"
                });

                const requestBody = {
                    maxResults: 3000,
                    query: searchTerm,
                    rangeStatus: ['ACTIVE', 'DISCONTINUED'],
                    categoryDescriptionFilter: categories.category,
                    superCategoryDescriptionFilter: categories.superCategory,
                    costPromotions: filters.isPromoOnly,
                    nisaOwnBrandFilter: filters.isHeritageOwnLabel,
                    coopOwnBrandFilter: filters.isCoopOwnLabel,
                    isOrganic: filters.isOrganic,
                    isGlutenFree: filters.isGlutenFree,
                    isVegetarian: filters.isVegetarian,
                    isFairtrade: filters.isFairtrade,
                    isHalal: filters.isHalal,
                    isVegan: filters.isVegan,
                    supplierCode: supplierCode
                };
                axios
                    .post(
                        window.CONFIG['ws.product-search-public.products.activocSearch'].replace(
                            '{franchiseCode}',
                            userDetails.currentFranchise.code
                        ),
                        requestBody,
                        {
                            headers: {
                                Authorization: cas,
                                'Content-Type': 'application/json'
                            }
                        }
                    )
                    .then(response => {
                        const json = response.data;
                        setSearched(true);
                        if (json.length > 0) {
                            setSearchResults(json);
                            setLoading(false);
                        } else {
                            setSearchResults([]);
                            setLoading(false);
                        }
                    })
                    .catch(() => {
                        setSearchResults([]);
                        setLoading(false);
                        alert.error('ERROR: Unable to retrieve search results.');
                    });
            } else {
                setSearched(false);
            }
        }
    }

    const clearSearch = () => {
        setSearchTerm('');
        setCategories(() => initialCategories);
        setFilters(() => initialFilters);
        setSearched(false);
    }

    const showCountMessage = (showCount && !isNaN(resultCount)) &&
        resultCount + ' Product' + (resultCount !== 1 ? 's' : '');

    const appliedProductFeatureFilters = Object.entries(filters).map(filter => (
        <Fade in={filters[filter[0]]} key={filter[0]} unmountOnExit>
            <button className={styles.filterLabel} onClick={() => setFilters({
                ...filters,
                [filter[0]]: null
            })}>{filter[0].substr(2).replace(/([A-Z])/g, ' $1').trim()}
            </button>
        </Fade>
    ));

    useEffect(() => {
        getCategories();
    }, [supplierCode, cas]);

    useEffect(() => {
        handleSubmit();
    }, [categories, filters]);

    return (
        <>
            <div className={`${styles.container} ${styles[style]}`}>
                <Button
                    onClick={() => setIsFiltersPaneOpen(true)}
                    className={styles.filtersBtn}
                    title={'Filters button'}
                >
                    <IconWrapper icon={faFilter}/>
                    Filters
                </Button>
                <FormControl
                    className={styles.searchField}
                    type={'search'}
                    onChange={event => setSearchTerm(event.target.value)}
                    value={searchTerm}
                    placeholder={'code / description / brand / barcode'}
                    onKeyPress={event => {
                        if (event.key === 'Enter') {
                            handleSubmit();
                        }
                    }}
                    aria-label={'Search Term'}
                />
                <Button
                    id={'product-search-btn'}
                    onClick={handleSubmit}
                    className={'search-submit'}
                    title={'Search'}
                >
                    <IconWrapper icon={faSearch}/>
                    Search
                </Button>
                {searched && (
                    <Button
                        variant={'secondary'}
                        onClick={clearSearch}
                        className={'search-clear'}
                        title={'Clear Search'}
                    >
                        <IconWrapper icon={faSyncAlt}/>
                        Clear Search
                    </Button>
                )}
                <div className={styles.resultsCount}>
                    {showCount && (
                        <span className={'results-returned'}>{showCountMessage}</span>)
                    }
                </div>
            </div>

            {(!allFiltersCleared || superCategorySelected || categorySelected) &&
            <div className={styles.filtersContainer}>

                <Fade in={superCategorySelected} unmountOnExit>
                    <button className={styles.filterLabel} onClick={() => setCategories({
                        ...categories,
                        superCategory: ''
                    })}>{categories.superCategory}
                    </button>
                </Fade>

                <Fade in={categorySelected} unmountOnExit>
                    <button className={styles.filterLabel} onClick={() => setCategories({
                        ...categories,
                        category: ''
                    })}>{categories.category}
                    </button>
                </Fade>

                {appliedProductFeatureFilters}

            </div>
            }
            <CSSTransition
                in={isFiltersPaneOpen}
                timeout={500}
                classNames={{
                    enter: styles.filtersPaneEnter,
                    enterActive: styles.filtersPaneEnterActive,
                    exit: styles.filtersPaneExit,
                    exitActive: styles.filtersPaneExitActive
                }}
                unmountOnExit
            >
                <SearchFiltersPane
                    categoriesToDisplay={allSupplierCategories}
                    supplierCode={supplierCode ? supplierCode : currentSupplierCode}
                    hideFilters={hideFilters}
                    selectedFilters={filters}
                    onFiltersUpdate={newFilters => setFilters(newFilters)}
                    selectedCategories={categories}
                    onCategoriesUpdate={newCategories => setCategories(newCategories)}
                    onClose={() => setIsFiltersPaneOpen(false)}
                />
            </CSSTransition>
        </>
    );
}
